import React, { useEffect, useCallback } from "react";
import { getItem } from '@/shared/utils/storage';
import { loadFilters, saveFilters } from '@/shared/utils/filterStorage';
import { formatYmdToDate } from "@/shared/utils/apiResponseHelpers";
import { DeliveryHistoryFilter } from "@/features/rider/deliveryHistory/types/deliveryHistory.types";
import { useDeliveryFilter, FILTER_STORAGE_KEY } from "@/features/rider/deliveryHistory/hooks/useDeliveryFilter";
import { useDeliveryOptions } from "@/features/rider/deliveryHistory/hooks/useDeliveryOptions";
import { useDeliveryData } from "@/features/rider/deliveryHistory/hooks/useDeliveryData";

export const useDeliveryHistory = () => {
    const userType = getItem<string>('USER_TYPE');
    const userId = getItem<string>('USER_ID');
    const isSupplierUser = userType === 'SUPPLIER' && !!userId;

    const {
        filter,
        setFilter,
        searchedFilter,
        setSearchedFilter,
        handleFilterChange,
        defaultStartDate,
        defaultEndDate,
    } = useDeliveryFilter(isSupplierUser, userId);

    const { partners, partnersLoading, statusCodes, statusLoading } = useDeliveryOptions(userType);

    const handleDateMetaChange = useCallback(
        (fromDate?: string, toDate?: string) => {
            setFilter((prev) => ({
                ...prev,
                startDate: fromDate ? formatYmdToDate(fromDate) : prev.startDate,
                endDate: toDate ? formatYmdToDate(toDate) : prev.endDate,
            }));
        },
        [setFilter]
    );

    const {
        deliveryHistoryData,
        loading,
        error,
        page,
        limit,
        totals,
        pageTotals,
        handlePageChange,
        resetPage,
        fetchDeliveryHistory,
    } = useDeliveryData(filter, handleDateMetaChange);

    // 초기 마운트
    useEffect(() => {
        const savedFilters = loadFilters<DeliveryHistoryFilter>(FILTER_STORAGE_KEY);
        if (savedFilters || filter.partnerId || filter.startDate || filter.endDate) {
            fetchDeliveryHistory();
            if (savedFilters) setSearchedFilter(savedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSearch = useCallback(() => {
        resetPage();
        fetchDeliveryHistory();
        saveFilters(FILTER_STORAGE_KEY, filter);
        setSearchedFilter({ ...filter });
    }, [filter, fetchDeliveryHistory, resetPage, setSearchedFilter]);

    const handleReset = useCallback(() => {
        const defaultFilter: DeliveryHistoryFilter = {
            startDate: defaultStartDate,
            endDate: defaultEndDate,
            status: '',
            partnerId: isSupplierUser ? userId || '' : '',
        };
        setFilter(defaultFilter);
        resetPage();
        setSearchedFilter(null);
        saveFilters(FILTER_STORAGE_KEY, defaultFilter);
    }, [defaultStartDate, defaultEndDate, isSupplierUser, userId, setFilter, resetPage, setSearchedFilter]);

    const renderOperationStatus = useCallback((statusName?: string) => {
        const text = statusName?.trim() || '운행 종료';
        const isEnded = /종료/.test(text);
        return React.createElement('span', { className: `status-badge ${isEnded ? 'status-badge--ended' : 'status-badge--active'}` }, text);
    }, []);

    return {
        deliveryHistoryData,
        loading,
        error,
        filter,
        searchedFilter,
        page,
        limit,
        totals,
        pageTotals,
        partners,
        partnersLoading,
        statusCodes,
        statusLoading,
        userType,
        handleFilterChange,
        handleSearch,
        handleReset,
        handlePageChange,
        renderOperationStatus,
        refetch: fetchDeliveryHistory,
    };
};
